import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Loader2, Users, Clock, GitBranch, Link2, ExternalLink, FileDown, CircleDot, CheckCircle2, Circle, XCircle, AlertTriangle } from "lucide-react";

const fmtHours = (h) => `${Number(h || 0).toLocaleString("es-ES", { maximumFractionDigits: 1 })} h`;
const fmtEur = (n) => {
  const x = Number(n) || 0;
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M€`;
  if (x >= 1_000)     return `${Math.round(x / 1_000)}k€`;
  return `${Math.round(x)}€`;
};

// Iter224 · Descarga del PDF Cliente 360º
async function downloadCliente360Pdf(offerId, offerTitle) {
  try {
    const resp = await axios.get(`${API}/offers/${offerId}/cliente360/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const link = document.createElement("a");
    link.href = url;
    const slug = (offerTitle || "cliente360").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60);
    link.download = `cliente360_${slug}_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 400);
  } catch (e) {
    alert(e?.response?.data?.detail || "No se pudo generar el PDF");
  }
}

// Iter224 · Milestone visual en el timeline
function TimelineMilestone({ ms, isLast }) {
  const state = ms.status || "pending";
  const level = ms.level;
  let Icon, iconCls, lineCls;
  if (level === "error")      { Icon = XCircle;       iconCls = "bg-rose-100 text-rose-700 border-rose-200";           lineCls = "bg-rose-200"; }
  else if (level === "success"){ Icon = CheckCircle2;  iconCls = "bg-emerald-100 text-emerald-700 border-emerald-200";  lineCls = "bg-emerald-300"; }
  else if (state === "done")   { Icon = CheckCircle2;  iconCls = "bg-[#E8F5E9] text-[#007934] border-green-200";        lineCls = "bg-[#B7DFC1]"; }
  else if (state === "current"){ Icon = CircleDot;     iconCls = "bg-blue-100 text-blue-700 border-blue-200 ring-2 ring-blue-300 ring-offset-1"; lineCls = "bg-blue-100"; }
  else                         { Icon = Circle;        iconCls = "bg-gray-50 text-gray-300 border-gray-200";            lineCls = "bg-gray-100"; }

  const fecha = (ms.at || "").slice(0, 10);
  return (
    <div className="flex gap-3" data-testid={`timeline-milestone-${ms.key}`}>
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${iconCls}`}>
          <Icon size={14} />
        </div>
        {!isLast && <div className={`w-0.5 flex-1 mt-1 ${lineCls}`} style={{ minHeight: 24 }}/>}
      </div>
      <div className={`flex-1 pb-4 ${state === "pending" ? "opacity-60" : ""}`}>
        <div className="flex items-baseline justify-between gap-2">
          <p className={`text-sm font-semibold ${level === "error" ? "text-rose-700" : level === "success" ? "text-emerald-700" : "text-gray-900"}`}>{ms.label}</p>
          {fecha && <span className="text-[10px] text-gray-400 whitespace-nowrap">{fecha}</span>}
        </div>
        {ms.actor && <p className="text-[11px] text-gray-500">{ms.actor}</p>}
        {ms.note && <p className="text-[11px] text-gray-500 mt-0.5 italic">{ms.note}</p>}
      </div>
    </div>
  );
}

/**
 * Iter223 · Cliente 360º — Panel lateral en OfferDetail que muestra la vida
 * completa de la oferta TIC: licitación vinculada, proyectos de horas
 * relacionados y proyectos DevOps.
 */
export default function OfferCliente360({ offerId, onOpenLinkModal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!offerId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/offers/${offerId}/related`);
        if (alive) setData(data);
      } catch (e) {
        console.warn("cliente360 no disponible", e);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [offerId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center justify-center h-40" data-testid="cliente360-loading">
        <Loader2 size={20} className="animate-spin text-gray-300" />
      </div>
    );
  }
  if (!data) return null;

  const lic = data.linked_licitacion;
  const projs = data.horas_projects || [];
  const hs = data.hours_summary || {};
  const dev = data.devops_projects || [];
  const timeline = data.timeline || [];
  const handleDownload = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      await downloadCliente360Pdf(offerId, data?.offer?.title);
    } finally { setDownloadingPdf(false); }
  };

  return (
    <div className="space-y-4" data-testid="cliente360-panel">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 text-white flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest opacity-70">Cliente 360º</p>
          <h3 className="text-lg font-bold font-['Outfit'] mt-0.5">Trazabilidad de la oferta</h3>
          <p className="text-xs opacity-80 mt-1">Timeline + licitación asociada + proyectos DevOps + horas imputadas.</p>
        </div>
        <button
          onClick={handleDownload}
          disabled={downloadingPdf}
          data-testid="cliente360-export-pdf-btn"
          className="bg-white text-slate-900 text-xs font-semibold px-3 py-2 rounded-lg hover:bg-slate-100 disabled:opacity-60 flex items-center gap-1.5 whitespace-nowrap"
          title="Exportar el panel Cliente 360º como PDF ejecutivo (para comité mensual)"
        >
          {downloadingPdf ? <Loader2 size={12} className="animate-spin"/> : <FileDown size={12}/>}
          Exportar PDF
        </button>
      </div>

      {/* Iter224 · Timeline visual */}
      {timeline.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="cliente360-timeline">
          <p className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-1.5">
            <AlertTriangle size={12} className="text-slate-500 rotate-180" style={{ transform: "none" }}/>
            Línea temporal
          </p>
          <div>
            {timeline.map((ms, i) => (
              <TimelineMilestone key={ms.key} ms={ms} isLast={i === timeline.length - 1} />
            ))}
          </div>
        </div>
      )}

      {/* Licitación vinculada */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="cliente360-licitacion">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
            <Link2 size={12} className="text-blue-600" /> Licitación en CRM
          </p>
          <button
            onClick={onOpenLinkModal}
            className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold uppercase"
            data-testid="cliente360-open-link-modal"
          >
            {lic ? "Cambiar" : "Vincular"} →
          </button>
        </div>
        {lic ? (
          <div className="space-y-1.5">
            <p className="font-semibold text-sm text-gray-900">{lic.titulo || "—"}</p>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
              {lic.expediente && <span>Exp. <span className="font-mono text-gray-700">{lic.expediente}</span></span>}
              {lic.municipio && <span>{lic.municipio}</span>}
              {lic.estado_licitacion && (
                <span className="inline-block bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded font-semibold uppercase text-[9px]">
                  {lic.estado_licitacion}{lic.fase_adjudicacion ? ` · ${lic.fase_adjudicacion}` : ""}
                </span>
              )}
            </div>
            {(lic.importe_con_ute || lic.presupuesto) && (
              <p className="text-xs text-gray-600 mt-1">
                Importe: <b className="text-[#007934]">{fmtEur(lic.importe_con_ute || lic.presupuesto)}</b>
              </p>
            )}
            {lic.fecha_presentacion && (
              <p className="text-[10px] text-gray-400">Presentada: {lic.fecha_presentacion}</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">Sin licitación vinculada. Usa el botón &quot;Vincular&quot; para asociar una del CRM o crear una nueva como PRESENTADA.</p>
        )}
      </div>

      {/* Proyectos DevOps relacionados */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="cliente360-devops">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
          <GitBranch size={12} className="text-indigo-600" /> Proyectos DevOps ({dev.length})
        </p>
        {dev.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Sin proyectos DevOps detectados con este municipio/tokens.</p>
        ) : (
          <ul className="space-y-1.5">
            {dev.map(p => (
              <li key={p.id} className="border border-gray-100 rounded-lg px-3 py-2 hover:bg-gray-50" data-testid={`cliente360-devops-${p.id}`}>
                <p className="text-xs font-semibold text-gray-900 line-clamp-2">{p.title}</p>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                  {p.status && <span className="uppercase bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{p.status}</span>}
                  {typeof p.workitems_open === "number" && <span>🟢 {p.workitems_open} abiertos</span>}
                  {typeof p.workitems_closed === "number" && <span>✔ {p.workitems_closed} cerrados</span>}
                  {p.azure_devops_project && <span title="Azure DevOps project" className="text-indigo-500">Azure · {p.azure_devops_project}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Horas imputadas + proyectos "horas" relacionados */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="cliente360-horas">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
            <Clock size={12} className="text-amber-600" /> Horas imputadas
          </p>
          {hs.total_hours > 0 && (
            <span className="text-lg font-bold text-amber-700 font-['Outfit']" data-testid="cliente360-horas-total">
              {fmtHours(hs.total_hours)}
            </span>
          )}
        </div>
        {projs.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Sin proyectos de &quot;Gestión de horas&quot; relacionados.</p>
        ) : (
          <>
            {(hs.by_project || []).slice(0, 4).map(row => (
              <div key={row.proyecto_id} className="flex items-center justify-between text-[11px] border-b border-gray-50 py-1" data-testid={`cliente360-hours-project-${row.proyecto_id}`}>
                <span className="truncate max-w-[180px]" title={row.nombre}>
                  <span className="font-mono text-gray-400 mr-1">{row.codigo_proyecto || "—"}</span>
                  {row.nombre}
                </span>
                <span className="text-gray-700 whitespace-nowrap ml-2">
                  <b className="text-amber-700">{fmtHours(row.horas)}</b>
                  <span className="text-gray-400 ml-1">({row.imputaciones})</span>
                </span>
              </div>
            ))}
            {(hs.by_collaborator || []).length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-50">
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
                  <Users size={10}/> Top colaboradores
                </p>
                <div className="flex flex-wrap gap-1">
                  {hs.by_collaborator.slice(0, 4).map(c => (
                    <span key={c.email} className="text-[10px] bg-amber-50 text-amber-800 border border-amber-100 px-1.5 py-0.5 rounded" title={c.email}>
                      {(c.email || "—").split("@")[0]} · <b>{fmtHours(c.horas)}</b>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {projs.length > (hs.by_project?.length || 0) && (
              <p className="text-[10px] text-gray-400 mt-2 italic">
                {projs.length - (hs.by_project?.length || 0)} proyectos relacionados adicionales sin horas imputadas.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
